// THE JARGON MAP — one source of truth for every word shown in the IMS container realm.
// Rule: the screen speaks plain English. Codes/enums only appear inside the "Ops details" panel.

/** Business status buckets, in the order they should always appear. */
export type BizStatus =
  | 'IN_STOCK'
  | 'ON_LEASE'
  | 'RETURNED'
  | 'RESERVED'
  | 'WIP'
  | 'SOLD'
  | 'SCRAPPED'
  | 'INTERNAL'
  | 'REPAIR'
  | 'DISPUTE'
  | 'RESALE'
  | 'OTHER';

export const BIZ_STATUS_ORDER: BizStatus[] = [
  'IN_STOCK',
  'ON_LEASE',
  'RETURNED',
  'RESERVED',
  'WIP',
  'SOLD',
  'RESALE',
  'REPAIR',
  'DISPUTE',
  'SCRAPPED',
  'INTERNAL',
  'OTHER',
];

export const BIZ_STATUS_LABEL: Record<BizStatus, string> = {
  IN_STOCK: 'In stock',
  ON_LEASE: 'On lease',
  RETURNED: 'Returned',
  RESERVED: 'Reserved for a customer',
  WIP: 'WIP',
  SOLD: 'Sold',
  RESALE: 'Held for resale',
  REPAIR: 'In repair',
  DISPUTE: 'In dispute',
  SCRAPPED: 'Scrapped',
  INTERNAL: 'Used by Crystal',
  OTHER: 'Other',
};

/** Short label for matrix column headers where space is tight. */
export const BIZ_STATUS_SHORT: Record<BizStatus, string> = {
  IN_STOCK: 'In stock',
  ON_LEASE: 'On lease',
  RETURNED: 'Returned',
  RESERVED: 'Reserved',
  WIP: 'WIP',
  SOLD: 'Sold',
  RESALE: 'For resale',
  REPAIR: 'In repair',
  DISPUTE: 'Dispute',
  SCRAPPED: 'Scrapped',
  INTERNAL: 'Crystal use',
  OTHER: 'Other',
};

export const BIZ_STATUS_PILL: Record<BizStatus, string> = {
  IN_STOCK: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ON_LEASE: 'bg-blue-50 text-blue-700 border-blue-200',
  RETURNED: 'bg-amber-50 text-amber-700 border-amber-200',
  RESERVED: 'bg-violet-50 text-violet-700 border-violet-200',
  WIP: 'bg-amber-50 text-amber-700 border-amber-200',
  SOLD: 'bg-slate-100 text-slate-700 border-slate-300',
  RESALE: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  REPAIR: 'bg-orange-50 text-orange-700 border-orange-200',
  DISPUTE: 'bg-rose-50 text-rose-700 border-rose-200',
  SCRAPPED: 'bg-zinc-100 text-zinc-600 border-zinc-300',
  INTERNAL: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  OTHER: 'bg-slate-50 text-slate-600 border-slate-200',
};

/** Map any raw sheet `Current` value or NormalizedContainer.status enum to a business status. */
export function toBizStatus(raw: string | null | undefined): BizStatus {
  const s = String(raw ?? '').trim().toUpperCase();
  if (!s) return 'OTHER';
  if (s === 'STOCK' || s.startsWith('IN STOCK') || s === 'AVAILABLE') return 'IN_STOCK';
  if (s === 'DEPLOYED' || s.startsWith('LEASE') || s.startsWith('ON LEASE') || s.startsWith('ON HIRE')) return 'ON_LEASE';
  if (s.startsWith('OFFLEASE') || s.startsWith('OFF LEASE') || s.startsWith('OFF-LEASE') || s.startsWith('RETURN')) return 'RETURNED';
  if (s === 'SALE' || s === 'SOLD' || s.startsWith('SALE')) return 'SOLD';
  // `includes`, not `startsWith`: the sheet also writes "MACHINE BLOCKED", which
  // the stock page already treats as a hold. Missing it here left 5 reserved
  // containers under Other on this page while the backend counted them Blocked.
  if (s === 'BLOCKED' || s.includes('BLOCK')) return 'RESERVED';
  if (s === 'TRADING' || s.startsWith('TRADE')) return 'RESALE';
  if (s.includes('REPAIR')) return 'REPAIR';
  if (s.includes('DISPUTE')) return 'DISPUTE';
  if (s === 'CONSUMED' || s.includes('CONSUMED') || s.includes('SCRAP')) return 'SCRAPPED';
  if (s.includes('CRYSTAL') || s.includes('OFFICE') || s.includes('CABIN')) return 'INTERNAL';
  // Work-in-progress: a freshly-bought unit still going through the purchase/AC
  // FMS pipeline (paid vs not-paid is resolved there). Was falling into OTHER.
  if (s === 'WIP' || s.startsWith('WIP')) return 'WIP';
  return 'OTHER';
}

export function bizStatusLabel(raw: string | null | undefined): string {
  return BIZ_STATUS_LABEL[toBizStatus(raw)];
}

/** Payment status → plain English. */
export const PAYMENT_LABEL: Record<string, string> = {
  PAID: 'Paid',
  PENDING: 'Payment due',
  NOT_PAID: 'Not paid',
  PARTIAL: 'Part paid',
  UNKNOWN: 'No bill found',
  'Not in A/C': 'Not yet with accounts',
  'Process by AP': 'With accounts',
};

export function paymentLabel(s: string | null | undefined): string {
  if (!s) return 'No bill found';
  return PAYMENT_LABEL[s] ?? PAYMENT_LABEL[String(s).toUpperCase()] ?? String(s);
}

/** Movement / transaction sub-types → plain English. */
export const MOVEMENT_LABEL: Record<string, string> = {
  PROCUREMENT: 'Bought',
  OFFLEASE_RETURN: 'Returned from lease',
  LEASE: 'Leased out',
  SALE: 'Sold',
  TRIP: 'Sent on trip',
  DEPLOYMENT: 'Deployed',
};

export function movementLabel(s: string | null | undefined): string {
  if (!s) return '—';
  return MOVEMENT_LABEL[String(s).toUpperCase()] ?? String(s);
}

export const DIRECTION_LABEL: Record<string, string> = { IN: 'Came in', OUT: 'Went out' };

export const DELIVERY_LABEL: Record<string, string> = {
  ALLOCATED: 'Assigned',
  DISPATCHED: 'On the way',
  DELIVERED: 'Delivered',
};

/** Stock-health bands. */
export const HEALTH_LABEL: Record<string, string> = {
  BELOW_MIN: 'Short of stock',
  'BELOW MIN': 'Short of stock',
  AT_MIN: 'At the minimum',
  'AT MIN': 'At the minimum',
  WITHIN: 'Healthy',
  AT_MAX: 'At the maximum',
  'AT MAX': 'At the maximum',
  ABOVE_MAX: 'Overstocked',
  'ABOVE MAX': 'Overstocked',
  UNKNOWN: 'No plan set',
  OUT_OF_STOCK: 'Out of stock',
};

export function healthLabel(s: string | null | undefined): string {
  if (!s) return 'No plan set';
  return HEALTH_LABEL[String(s).toUpperCase().replace(/[\s-]/g, '_')] ?? String(s);
}

/** Exception severity + category. */
export const SEVERITY_LABEL: Record<string, string> = {
  high: 'Fix now',
  medium: 'Fix this week',
  low: 'Tidy up',
};

export const EXCEPTION_LABEL: Record<string, string> = {
  missing_field: 'Missing information',
  malformed_id: 'Container number looks wrong',
  near_duplicate_name: 'Same client spelled several ways',
  impossible_value: "Year of make can't be right",
  stale_state: 'Sitting too long',
  stub_row: 'Empty record (number only)',
  test_entry: 'Gate-log entry with no container record',
};

/** Field labels — use these instead of the sheet's column names. */
export const FIELD: Record<string, string> = {
  yom: 'Year made',
  grade: 'Condition',
  condition: 'Condition',
  productCode: 'Product code',
  type: 'Type',
  size: 'Size',
  yard: 'Yard',
  customer: 'Customer',
  days: 'Days in current status',
  daysOnLease: 'Days with client',
  daysIdle: 'Days idle',
  lastDeployed: 'On lease since',
  agreementEnd: 'Contract ends',
  rate: 'Monthly rate (₹)',
  sourceRow: 'Source sheet row',
  currentRaw: 'Sheet status',
  vendor: 'Supplier',
  buyer: 'Sold to',
  spend: 'Amount spent',
  share: 'Share of spend',
  purchaseValue: 'Purchase cost',
  salePrice: 'Sale price',
  margin: 'Profit',
  stockValue: 'Money tied up in unsold stock',
  salesRevenue: 'Sales income',
  pendingPayments: 'Bills we still owe suppliers',
};

/** Page titles — the sidebar and the page header must agree. */
export const VIEW_TITLE: Record<string, string> = {
  overview: 'Overview',
  'on-lease': 'On Lease',
  'off-lease': 'Returned Containers',
  sold: 'Sold',
  transactions: 'Movements',
  financials: 'Money',
  vendors: 'Suppliers',
  exceptions: 'Things to fix',
  aging: 'How long they have been sitting',
  'stock-health': 'Stock Health',
  analytics: 'Fleet Map',
  finder: 'Find a container',
  settings: 'Settings',
};
